import { useState, useCallback, useRef, useEffect } from 'react';
import { handleApiError, ProcessedApiError } from '../utils/apiUtils';

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: ProcessedApiError | null;
  execute: (apiCall: () => Promise<T>) => Promise<void>;
  reset: () => void;
  clearError: () => void;
  retry: (() => Promise<void>) | null;
}

/**
 * Custom hook for managing API calls and their state
 */
export const useApi = <T = any>(): UseApiState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ProcessedApiError | null>(null);
  const [lastApiCall, setLastApiCall] = useState<(() => Promise<T>) | null>(null);
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(async (apiCall: () => Promise<T>) => {
    if (!isMountedRef.current) return;

    setLoading(true);
    setError(null);
    setLastApiCall(() => apiCall);

    try {
      const result = await apiCall();
      
      if (isMountedRef.current) {
        setData(result);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const processedError = handleApiError(err as Error);
        setError(processedError);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    if (!isMountedRef.current) return;
    
    setData(null);
    setLoading(false);
    setError(null);
    setLastApiCall(null);
  }, []);

  const clearError = useCallback(() => {
    if (!isMountedRef.current) return;
    
    setError(null);
  }, []);

  const retry = useCallback(async () => {
    if (!lastApiCall || !isMountedRef.current) return;
    
    await execute(lastApiCall);
  }, [lastApiCall, execute]);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    clearError,
    retry: lastApiCall ? retry : null,
  };
};
